/* eslint-disable no-console */
import FetchClient from "./FetchClient";
import { CONTENT_TYPES } from "../typings/document-types";
import { MenuItem, ShowSearchResult } from "../typings/typings";
import { isBrowserContext } from "../util/ContextHelper";
import { contentByPathAdapter } from "./adapters";
import configuration from "../Configuration";

export enum HeadlessAgent {
    Client = "Client",
    Server = "Server"
}

export interface HeadlessResponse<T = {}> {
    result: T;
    statusCode: number;
    statusText?: string;
    agent: HeadlessAgent;
    redirectUrl?: string;
}

export default class UmbracoClient
{
    baseClient: FetchClient;

    constructor(api: FetchClient)
    {
        this.baseClient = api;
        this.baseClient.defaultHeaders = {
            "Accept": "application/json",
            "X-Request-Agent": isBrowserContext() ? HeadlessAgent.Client : HeadlessAgent.Server,
            "X-Request-Domain": globalThis.origin,
            "Origin": globalThis.origin,
            "Api-Key": configuration.apiKey,
        };
    }

    async getMenu(): Promise<MenuItem[]>
    {
        const url = this.baseClient.urlBuilder("/api/ExtendedMenu/").toString();
        return await this.baseClient.fetchGet<MenuItem[]>(url);
    }

    async getShows(): Promise<ShowSearchResult[]>
    {
        const url = this.baseClient.urlBuilder("/api/shows/").toString();
        return await this.baseClient.fetchGet<ShowSearchResult[]>(url);
    }

    // async search(query: string): Promise<SearchResult[]>
    // {
    //     const url = this.baseClient.urlBuilder("api/search", [{ name: "query", value: query }]).toString();
    //     const searchResults: SearchResult[] = await this.baseClient.fetchGet(url);
    //     return searchResults;
    // }

    async getRootPages(): Promise<HeadlessResponse<CONTENT_TYPES[]>>
    {
        const url = this.baseClient.urlBuilder("/api/content/").toString();
        return await this.baseClient.fetchGet<HeadlessResponse<CONTENT_TYPES[]>>(url);
    }

    async getById<T = CONTENT_TYPES>(id: number | string): Promise<HeadlessResponse<T>>
    {
        const url = this.baseClient.urlBuilder(`/api/content/${id}`).toString();
        return await this.baseClient.fetchGet<HeadlessResponse<T>>(url);
    }

    async getByRoute<T extends CONTENT_TYPES>(route: string): Promise<HeadlessResponse<T>>
    {
        let preview = false;
        let finalRoute = route;
        if (route.startsWith("/preview"))
        {
            finalRoute = route.replace("/preview", "");
            preview = true;
        }
        if (finalRoute.startsWith("/"))
        {
            finalRoute = finalRoute.substring(1);
        }
        const url = this.baseClient.urlBuilder(`/umbraco/delivery/api/v2/content/item/${finalRoute}?fields=properties[$all[properties[$all]]]&expand=properties[$all[properties[$all[properties[$all]]]]]`).toString();
        const response = await this.baseClient.fetchGet<any>(url, preview);

        return contentByPathAdapter<T>(response);
    }

    async getByType<T = CONTENT_TYPES>(contentTypeAlias: string): Promise<HeadlessResponse<T>>
    {
        const url = this.baseClient.urlBuilder("/api/content/type/", { contentType: contentTypeAlias }).toString();
        return await this.baseClient.fetchGet<HeadlessResponse<T>>(url);
    }

    async getAncestors<T = CONTENT_TYPES[]>(id: number): Promise<HeadlessResponse<T>>
    {
        const url = this.baseClient.urlBuilder(`/api/content/${id}/ancestors/`).toString();
        return await this.baseClient.fetchGet<HeadlessResponse<T>>(url);
    }

    async getChildren<T = CONTENT_TYPES>(id: number): Promise<HeadlessResponse<T>>
    {
        const url = this.baseClient.urlBuilder(`api/content/${id}/children/`).toString();
        return await this.baseClient.fetchGet<HeadlessResponse<T>>(url);
    }

    async getDecendants<T = CONTENT_TYPES[]>(id: number): Promise<HeadlessResponse<T>>
    {
        const url = this.baseClient.urlBuilder(`/api/content/${id}/decendants/`).toString();
        return await this.baseClient.fetchGet<HeadlessResponse<T>>(url);
    }
}
